import { useLocation, Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import Footer from '../../components/Footer';
import articlesData from '../../data/articles.json';
import articleImages from '../../data/articleImages';
import '../Pages.css';

function ArticleDetail() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const id = queryParams.get('id');
  const article = articlesData.find(a => a.id === parseInt(id));

  // Combine content if it's an array, otherwise use it as-is
  const getContent = (article) => {
    if (!article) return '';
    if (Array.isArray(article.content)) {
      return article.content.join('\n\n');
    }
    return article.content || '';
  };

  const renderContent = (content) => {
    const blocks = content.split('\n\n');

    // Function to parse text and convert markdown-style links to anchor tags
    const parseLinks = (text) => {
      const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = linkRegex.exec(text)) !== null) {
        // Add text before the link
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }
        // Add the link
        parts.push(
          <a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer">
            {match[1]}
          </a>
        );
        lastIndex = match.index + match[0].length;
      }

      // Add remaining text after last link
      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }

      return parts.length > 0 ? parts : text;
    };

    return blocks.map((block, index) => {
      // Check if block is an image: ![alt text](image-name) or ![alt text](https://url)
      const imageRegex = /^!\[([^\]]*)\]\(([^)]+)\)$/;
      const imageMatch = block.trim().match(imageRegex);

      if (imageMatch) {
        const altText = imageMatch[1];
        const imagePath = imageMatch[2];

        // Check if it's a local image name (no protocol) or external URL
        let imageSrc = imagePath;
        if (!imagePath.startsWith('http://') && !imagePath.startsWith('https://') && !imagePath.startsWith('/')) {
          // It's a local image name - look it up in articleImages
          imageSrc = articleImages[imagePath] || imagePath;
        }

        return (
          <div key={index} className="article-image">
            <img src={imageSrc} alt={altText} />
            {altText && (
              <p className="article-image-caption">
                {altText.split('\n').map((line, i) => (
                  <span key={i}>
                    {line}
                    {i < altText.split('\n').length - 1 && <br />}
                  </span>
                ))}
              </p>
            )}
          </div>
        );
      }

      // Check if block is a bullet list (lines starting with * or -)
      if (block.trim().startsWith('*') || block.trim().startsWith('-')) {
        const items = block.split('\n').map(line => line.trim().replace(/^[*-]\s*/, ''));
        return (
          <ul key={index}>
            {items.map((item, i) => (
              <li key={i}>{parseLinks(item)}</li>
            ))}
          </ul>
        );
      }
      // Regular paragraph
      return <p key={index}>{parseLinks(block)}</p>;
    });
  };

  if (!article) {
    return (
      <div className="Page">
        <PageHeader />
        <div className="Page-container">
          <h1>Article Not Found</h1>
          <p className="no-results">The article you're looking for doesn't exist.</p>
          <Link to="/articles" className="back-link">← Back to Articles</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="Page">
      <PageHeader />
      <div className="Page-container">
        <Link to="/articles" className="back-link">← Back to Articles</Link>

        <article className="Page-section article-detail">
          <h1>{article.title}</h1>
          <p className="article-meta">
            By {article.author} | {new Date(article.date + 'T00:00:00').toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              timeZone: 'UTC'
            })}
          </p>
          <p className="article-summary"><strong>{article.summary}</strong></p>
          <div className="article-content">
            {renderContent(getContent(article))}
          </div>
        </article>
      </div>
      <Footer />
    </div>
  );
}

export default ArticleDetail;
